import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import InvoicePage from './InvoicePage';
import { RootState } from '../../redux/root';
import { getNextInvoiceNumber } from '../../api/fees';
import { createInvoicePdf, setInvoiceData } from './InvoiceUtil';
import {
  setInvoicePDF,
  setInvoicePdfForInvoice,
} from '../../redux/slices/feesSlice';
import ViewInvoiceModal from './ViewInvoiceModal';

type Props = {
  selectedContact?: any;
  reimbursableAttachment?: string[];
  fees: number[];
};
const InvoiceTool: FC<Props> = ({ selectedContact, reimbursableAttachment, fees }) => {
  const itemRefs = useRef([]);
  const createSingleDoc = useRef<boolean>(false);
  const { invoiceBody: body, invoiceHeader } = useSelector((s: RootState) => s.fees);
  const [pages, setPages] = useState<InvoicePage[]>([]);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [invoiceStatus, setInvoiceStatus] = useState<string>('');
  const dispatch = useDispatch();

  useEffect(() => {
    const loadData = async () => {
      if (selectedContact) {
        const fee_ids = fees.reduce((ids, id) => {
          if (!ids) {
            return `${id}`;
          }
          return `${ids},${id}`;
        }, '');
        const params = {
          fee_ids,
          contact_id: selectedContact.id,
        };
        const info = await getNextInvoiceNumber(params);
        const { data } = info;
        setInvoiceData(data, dispatch);
      }
    };
    loadData();
  }, [selectedContact]);
  useEffect(() => {
    if (body.length > 0 && body[0].lineItems.length > 0 && Object.keys(invoiceHeader).length > 0) {
      const MAX_ITEMS_PER_PAGE = 12;
      let counter = 0;
      let page;
      let _grandTotal = 0;
      const _pages = body[0].lineItems.reduce((pageArr, line) => {
        _grandTotal += Number(line.amount) * Number(line.quantity);
        if (counter === 0) {
          page = { lineItems: [] };
          pageArr.push(page);
        }
        page.lineItems.push(line);
        if (line.notes) {
          counter += 3;
        } else {
          counter++;
        }
        if (counter > MAX_ITEMS_PER_PAGE - 1) {
          counter = 0;
        }
        return pageArr;
      }, []);
      setPages(_pages);
      setGrandTotal(_grandTotal);
      setInvoiceStatus(invoiceHeader.invoiceStatus);
    }
  }, [body[0].lineItems.length, invoiceHeader]);
  useEffect(() => {
    // EA-1391: the invoice number these pages were laid out for. The finished
    // PDF is tagged with it so Send can confirm the attachment matches the
    // invoice in the payload, and so a stale PDF from a prior send on the same
    // project can never be emailed.
    const targetInvoiceNumber = invoiceHeader?.invoiceNumber || '';
    const createPdf = async () => {
      if (itemRefs.current.length > 0) {
        const _base64 = await createInvoicePdf(itemRefs, reimbursableAttachment);
        const prefix = /data:application\/pdf;filename=generated.pdf;base64,/;
        dispatch(
          setInvoicePdfForInvoice({
            pdf: _base64.replace(prefix, ''),
            invoiceNumber: targetInvoiceNumber,
          }),
        );
        setPages([]);
       }
    };
    if (itemRefs.current.length > 0 && pages.length > 0) {
      createSingleDoc.current = true;
      // EA-1391: a new PDF is about to be generated for this invoice. Drop any
      // previously-bound PDF synchronously so the brief window while this
      // generation runs can never let a stale attachment be sent.
      dispatch(setInvoicePDF(''));
      createPdf();
    }
  }, [pages]);
  return (
    <Wrapper>
      {pages.map((b, index) => {
        return (
          <InvoicePage
            grandTotal={grandTotal}
            ref={el => (itemRefs.current[index] = el)}
            key={index}
            lines={b.lineItems}
            isLastPage={index === pages.length - 1}
            totalPages={pages.length}
            index={index}
            invoiceStatus={invoiceStatus}
          />
        );
      })}
    </Wrapper>
  );
};

export default InvoiceTool;

const Wrapper = styled.div`
  height: 11in;
`;
